import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import * as DateUtils from './dateUtils';
import DayPicker from './DayPicker';
import * as DateFns from 'date-fns/fp';

const { Modifiers } = DateUtils;

const DateRangeWrapper = styled.div`
  user-select: none;
`;

const isSelectingFirstDay = (from, to, day) => {
  const isBeforeFirstDay = from && day < from;
  const isRangeSelected = !!(from && to);
  return !from || isBeforeFirstDay || isRangeSelected;
};

export const DateRangePicker = ({ value, defaultValue, defaultCursorRange, onChange, maxDate, minDate }) => {
  const [currentRange, setCurrentRange] = useState(value || defaultValue);
  const [from, to] = currentRange;
  const [cursorRange, setCursorRange] = useState(defaultCursorRange);
  const [month, setMonth] = useState(
    DateUtils.getInitialDate(
      {
        maxDate,
        minDate,
      },
      from
    )
  );

  const modifiers = {
    [Modifiers.SELECTED_RANGE_START]: from,
    [Modifiers.SELECTED_RANGE_END]: to,
    [Modifiers.SELECTED_RANGE]: day => DateUtils.isDayInRange(day, currentRange, true),
    [Modifiers.HOVERED_RANGE]: day => DateUtils.isDayInRange(day, cursorRange, true),
    [Modifiers.HOVERED_RANGE_START]: day => cursorRange[0] && DateFns.isSameDay(day, cursorRange[0]),
    [Modifiers.HOVERED_RANGE_END]: day => cursorRange[1] && DateFns.isSameDay(day, cursorRange[1]),
    [Modifiers.DISABLED]: day => !DateUtils.isDayInRange(day, [minDate, maxDate]),
  };

  const handleDayPickerChange = useCallback(e => {
    setMonth(e.target.value);
  }, []);

  const handleDayClick = useCallback(
    (day, modifiers, e) => {
      if (modifiers.disabled) {
        return;
      }

      let value;
      if (isSelectingFirstDay(from, to, day)) {
        value = [day, null];
        setCurrentRange(value);
        setCursorRange(value);
      } else {
        setCurrentRange(range => (value = [range[0], day]));
        setCursorRange([null, null]);
      }

      onChange &&
        onChange({
          ...e,
          target: {
            ...e.target,
            value,
          },
        });
    },
    [from, onChange, to]
  );

  const handleDayMouseEnter = useCallback(
    day => {
      if (cursorRange[0]) {
        setCursorRange(range => [range[0], day]);
      }
    },
    [cursorRange]
  );

  useEffect(() => {
    if (Array.isArray(value) && value !== currentRange) {
      setCurrentRange(value);
    }
  }, [currentRange, value]);

  return (
    <DateRangeWrapper>
      <DayPicker
        numberOfMonths={2}
        onChange={handleDayPickerChange}
        fromMonth={minDate}
        month={month}
        toMonth={maxDate}
        selectedDays={currentRange}
        modifiers={modifiers}
        onDayClick={handleDayClick}
        onDayMouseEnter={handleDayMouseEnter}
        canChangeMonth
      />
    </DateRangeWrapper>
  );
};

DateRangePicker.defaultProps = {
  defaultValue: [null, null],
  defaultCursorRange: [null, null],
  maxDate: DateUtils.getDefaultMaxDate(),
  minDate: DateUtils.getDefaultMinDate(),
};
